import { useScrollReveal } from "@/hooks/useScrollReveal";
import { EnfinityPrimaryBtn } from "@/components/design-system/enfinity/EnfinityButton";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function SuccessStories() {
  const { ref, isVisible } = useScrollReveal();

  const caseStudies = [
    {
      type: "shield",
      image: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/cleaning.png",
      overlayImage: null,
      badgeText: "EnfiniShield Case Study",
      badgeColor: "#8b5cf6",
      icon: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/clean.svg",
      iconGradient: "linear-gradient(137.738deg, #A78BFA, #8B5CF6)",
      title: "Cleaning Content Creator",
      stats: [
        { label: "MONTHLY REVENUE", value: "$3K+", color: "#8b5cf6" },
        { label: "CLAIMS MADE", value: "100+", color: "#8b5cf6" },
      ],
    },
    {
      type: "shield",
      image: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/asmr.png",
      badgeText: "EnfiniShield Case Study",
      badgeColor: "#8b5cf6",
      icon: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/mic.svg",
      iconGradient: "linear-gradient(137.738deg, #A78BFA, #8B5CF6)",
      title: "ASMR Content Creator",
      stats: [
        { label: "MONTHLY REVENUE", value: "$3K+", color: "#8b5cf6" },
        { label: "CLAIMS MADE", value: "4,500+", color: "#8b5cf6" },
      ],
    },
    {
      type: "boost",
      image: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/fruit.png",
      badgeText: "EnfiniBoost Case Study",
      badgeColor: "#3b82f6",
      icon: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/cook.svg",
      iconGradient: "linear-gradient(137.738deg, rgb(96, 165, 250) 2.173%, rgb(59, 130, 246) 97.827%)",
      title: "Cooking Channel Case",
      stats: [
        { label: "REVENUE GENERATED", value: "$215K", color: "#3b82f6" },
        { label: "VIEWS GAINED", value: "4.9B+", color: "#3b82f6" },
      ],
    },
    {
      type: "boost",
      image: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/story.png",
      badgeText: "EnfiniBoost Case Study",
      badgeColor: "#3b82f6",
      icon: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/book.svg",
      iconGradient: "linear-gradient(137.738deg, rgb(96, 165, 250) 2.173%, rgb(59, 130, 246) 97.827%)",
      title: "Story Telling | Faceless Channel Case",
      stats: [
        { label: "REVENUE GENERATED", value: "$175K", color: "#3b82f6" },
        { label: "VIEWS GAINED", value: "3.8B+", color: "#3b82f6" },
      ],
    },
  ];

  const niches = ["Gaming", "Music", "Education", "Vlogs", "Tech", "Entertainment"];

  return (
    <div
      id="success-stories"
      ref={ref}
      className={`${
        isVisible ? "" : ""
      } bg-white w-full xxs:px-2 xs:px-4 sm:px-4 md:px-6 lg:px-6 xl:px-8 xxs:py-12 xs:py-14 sm:py-16 md:py-18 lg:py-32`}
    >
      <div className="flex flex-col items-center w-full mx-auto xxs:px-4 xs:px-5 sm:px-6 md:px-7 lg:px-8 max-w-7xl">
        {/* SUCCESS STORIES Label */}
        <p className="font-medium text-[#757575] text-xs xxs:text-xs xs:text-sm sm:text-sm md:text-md lg:text-lg 4xl:text-[24px] text-center xxs:mb-4 xs:mb-5 sm:mb-6 md:mb-7 lg:mb-8 enfinity-header-font">
        How can you trust us?        </p>

        {/* Main Heading */}
        <div className="text-center xxs:mb-4 xs:mb-5 sm:mb-6 md:mb-7 lg:mb-8">
          <p className="text-2xl xxs:text-5xl xs:text-5xl sm:text-5xl md:text-5xl lg:text-[80px] 4xl:text-[90px] font-normal leading-tight xxs:leading-[1.3] xs:leading-[1.2] sm:leading-[1.15] md:leading-[1.1] lg:leading-tight xl:leading-[106px] text-black enfinity-header-font">
            Real results from{" "}
            <span
              className="bg-gradient-to-r from-[#00d115] via-[#00b0ff] to-[#9c44e9] bg-clip-text text-transparent enfinity-header-font"
              style={{ 
                WebkitTextFillColor: "transparent",
                backgroundImage: "linear-gradient(to right, #00d115 0%, #00b0ff 0%, #9c44e9 100%)"
              }}
            >
              real creators
            </span>
          </p>
        </div>

        {/* Description */}
        <p className="text-sm xxs:text-[1rem] xs:text-[1rem] lg:text-xl xl:text-2xl font-normal leading-relaxed text-center text-black max-w-3xl xxs:mb-8 xs:mb-10 sm:mb-12 md:mb-14 lg:mb-16 enfinity-subtext-font">
          See how Enfinity's services have transformed creator businesses with measurable growth and protection.
        </p>

        {/* Case Study Cards - First Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xxs:gap-4 xs:gap-5 sm:gap-6 md:gap-7 lg:gap-8 w-full xxs:mb-4 xs:mb-5 sm:mb-6 md:mb-7 lg:mb-8">
          {caseStudies.slice(0, 2).map((study, index) => (
            <div
              key={index}
              className="bg-white border border-[#ddd] rounded-[20px] overflow-hidden flex flex-col"
            >
              {/* Image Section */}
              <div className="relative flex-shrink-0 xxs:aspect-[16/9] xs:aspect-[16/8] sm:aspect-[16/7] md:aspect-[16/6.5] lg:aspect-[16/6] overflow-hidden">
                <img
                  src={study.image}
                  alt={study.title}
                  className="w-full h-full object-cover"
                />
                {study.overlayImage && (
                  <img
                    src={study.overlayImage}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                
                {/* Badge */}
                <div className="absolute xxs:top-3 xs:top-3 sm:top-4 md:top-4 lg:top-5 xxs:right-2 xs:right-3 sm:right-3 md:right-3 lg:right-4 bg-white flex items-center justify-center xxs:py-1 xs:py-1.5 sm:py-1.5 md:py-2 lg:py-2 xxs:px-2 xs:px-2.5 sm:px-3 md:px-3 lg:px-4 rounded-full">
                  <p
                    className="font-semibold xxs:text-xs xs:text-sm sm:text-base md:text-base lg:text-lg leading-normal whitespace-nowrap"
                    style={{ color: study.badgeColor }}
                  >
                    {study.badgeText}
                  </p>
                </div>
              </div>

              {/* Content Section */}
              <div className="flex flex-col flex-1 xxs:p-4 xs:p-4 sm:p-5 md:p-5 lg:p-6">
                {/* Icon and Title */}
                <div className="flex gap-4 items-center xxs:mb-4 xs:mb-4 sm:mb-5 md:mb-5 lg:mb-6">
                  <div
                    className="flex items-center justify-center p-2 rounded-lg flex-shrink-0 xxs:w-[50px] xxs:h-[50px] xs:w-[55px] xs:h-[55px] sm:w-[55px] sm:h-[55px] md:w-[58px] md:h-[58px] lg:w-[60px] lg:h-[60px]"
                    style={{ 
                      backgroundImage: study.iconGradient
                    }}
                  >
                    <img
                      src={study.icon}
                      alt=""
                      className="xxs:w-6 xxs:h-6 xs:w-7 xs:h-7 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-8 lg:h-8 object-contain"
                    />
                  </div>
                  <p className="font-medium xxs:text-lg xs:text-xl sm:text-xl md:text-2xl lg:text-2xl text-black leading-normal">
                    {study.title}
                  </p>
                </div>

                {/* Divider */}
                <div className="w-full border-t border-gray-300 xxs:my-4 xs:my-4 sm:my-5 md:my-5 lg:my-6"></div>

                {/* Stats */}
                <div className="flex items-center justify-between xxs:gap-2 xs:gap-3 sm:gap-3 md:gap-4 lg:gap-4 enfinity-header-font font-normal">
                  {study.stats.map((stat, statIndex) => (
                    <div
                      key={statIndex}
                      className="flex flex-col xxs:gap-1 xs:gap-1.5 sm:gap-1.5 md:gap-2 lg:gap-2 items-center justify-center flex-1"
                    >
                      <p className="font-normal xxs:text-[10px] xs:text-[13px] lg:text-base text-black/50 text-left w-full">
                        {stat.label}
                      </p>
                      <p
                        className="font-normal xxs:text-3xl xs:text-4xl sm:text-4xl md:text-5xl lg:text-6xl leading-normal w-full text-left"
                        style={{ color: stat.color }}
                      >
                        {stat.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Case Study Cards - Second Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xxs:gap-4 xs:gap-5 sm:gap-6 md:gap-7 lg:gap-8 w-full xxs:mb-8 xs:mb-10 sm:mb-12 md:mb-14 lg:mb-16">
          {caseStudies.slice(2, 4).map((study, index) => (
            <div
              key={index}
              className="bg-white border border-[#ddd] rounded-[20px] overflow-hidden flex flex-col"
            >
              {/* Image Section */}
              <div className="relative flex-shrink-0 xxs:aspect-[16/9] xs:aspect-[16/8] sm:aspect-[16/7.5] md:aspect-[16/7] lg:aspect-[16/7] overflow-hidden">
                <img
                  src={study.image}
                  alt={study.title}
                  className="w-full h-full object-cover"
                />
                
                {/* Badge */}
                <div className="absolute xxs:top-3 xs:top-3 sm:top-4 md:top-4 lg:top-5 xxs:right-2 xs:right-3 sm:right-3 md:right-3 lg:right-4 bg-white flex items-center justify-center xxs:py-1 xs:py-1.5 sm:py-1.5 md:py-2 lg:py-2 xxs:px-2 xs:px-2.5 sm:px-3 md:px-3 lg:px-4 rounded-full">
                  <p
                    className="font-semibold xxs:text-xs xs:text-sm sm:text-base md:text-base lg:text-lg leading-normal whitespace-nowrap"
                    style={{ color: study.badgeColor }}
                  >
                    {study.badgeText}
                  </p>
                </div>
              </div>

              {/* Content Section */}
              <div className="flex flex-col flex-1 xxs:p-4 xs:p-4 sm:p-5 md:p-5 lg:p-6">
                {/* Icon and Title */}
                <div className="flex gap-4 items-center xxs:mb-4 xs:mb-4 sm:mb-5 md:mb-5 lg:mb-6">
                  <div
                    className="flex items-center justify-center p-2 rounded-lg flex-shrink-0 xxs:w-[50px] xxs:h-[50px] xs:w-[55px] xs:h-[55px] sm:w-[55px] sm:h-[55px] md:w-[58px] md:h-[58px] lg:w-[60px] lg:h-[60px]"
                    style={{ 
                      backgroundImage: study.iconGradient
                    }}
                  >
                    <img
                      src={study.icon}
                      alt=""
                      className="xxs:w-6 xxs:h-6 xs:w-7 xs:h-7 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-8 lg:h-8 object-contain"
                    />
                  </div>
                  <p className="font-medium xxs:text-lg xs:text-xl sm:text-xl md:text-2xl lg:text-2xl text-black leading-normal">
                    {study.title}
                  </p>
                </div>

                {/* Divider */}
                <div className="w-full border-t border-gray-300 xxs:my-4 xs:my-4 sm:my-5 md:my-5 lg:my-6"></div>

                {/* Stats */}
                <div className="flex items-center justify-between xxs:gap-2 xs:gap-3 sm:gap-3 md:gap-4 lg:gap-4 enfinity-header-font font-normal">
                  {study.stats.map((stat, statIndex) => (
                    <div
                      key={statIndex}
                      className="flex flex-col xxs:gap-1 xs:gap-1.5 sm:gap-1.5 md:gap-2 lg:gap-2 items-center justify-center flex-1"
                    >
                      <p className="font-normal xxs:text-[10px] xs:text-[13px] lg:text-base text-black/50 text-left w-full">
                        {stat.label}
                      </p>
                      <p
                        className="font-normal xxs:text-3xl xs:text-4xl sm:text-4xl md:text-5xl lg:text-6xl leading-normal w-full text-left"
                        style={{ color: stat.color }}
                      >
                        {stat.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Case Studies Button */}
        <div className="xxs:mb-8 xs:mb-10 sm:mb-12 md:mb-14 lg:mb-16">
          <Link to="/case-studies">
            <EnfinityPrimaryBtn>
              <span className="flex items-center gap-2">
                View all case studies
                <ArrowRight className="xxs:w-4 xxs:h-4 xs:w-5 xs:h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-6 lg:h-6" />
              </span>
            </EnfinityPrimaryBtn>
          </Link>
        </div>

        {/* Footer Section */}
        <div className="flex flex-col items-center xxs:gap-4 xs:gap-5 sm:gap-6 md:gap-7 lg:gap-8 w-full">
          <p className="font-normal xxs:text-sm xs:text-base sm:text-base md:text-lg lg:text-lg text-black text-center enfinity-subtext-font">
            Trusted by creators in every niche
          </p>
          <div className="w-full overflow-hidden lg:overflow-visible">
            <style>{`
              @keyframes scroll-niches {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-50%);
                }
              }
              .niches-scroll-container {
                overflow: hidden;
                width: 100%;
              }
              .niches-scroll-content {
                display: flex;
                gap: 1rem;
                animation: scroll-niches 20s linear infinite;
                width: fit-content;
              }
              .niches-scroll-content:hover {
                animation-play-state: paused;
              }
              @media (min-width: 1024px) {
                .niches-scroll-container {
                  overflow: visible;
                }
                .niches-scroll-content {
                  animation: none;
                  display: flex;
                  flex-wrap: wrap;
                  justify-content: center;
                  gap: 1.75rem;
                  width: 100%;
                }
              }
            `}</style>
            <div className="niches-scroll-container">
              <div className="niches-scroll-content lg:flex lg:flex-wrap lg:justify-center lg:gap-8">
                {/* Duplicate niches for seamless loop on mobile */}
                {[...niches, ...niches].map((niche, index) => {
                  const isDuplicate = index >= niches.length;
                  return (
                    <div 
                      key={index} 
                      className={`flex xxs:gap-2 xs:gap-2.5 sm:gap-2.5 md:gap-3 lg:gap-3 items-center flex-shrink-0 ${isDuplicate ? 'lg:hidden' : ''}`}
                    >
                      <img
                        src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/youtube-icon.svg"
                        alt="YouTube"
                        className="xxs:w-6 xxs:h-6 xs:w-7 xs:h-7 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-8 lg:h-8 object-contain"
                      />
                      <p className="font-normal xxs:text-sm xs:text-base sm:text-base md:text-lg lg:text-lg text-black text-center enfinity-subtext-font whitespace-nowrap">
                        {niche}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
